import Joi from 'joi';
import PropTypes from 'prop-types';
import _ from 'lodash';

import Button from '../../themed/button';
import Conditional from '../../common/conditional';
import Form from '../../common/form';

import { IMAGE_LIMITS, IMAGE_TYPE_SELECTION, MATERIAL_OPTIONS, RT_TECHNIQUES, SOURCE_ACTIVITY, SOURCE_MANUFACTURERS, SOURCES } from '../../../utils/constants';
import { getFileUploadTypes } from '../../../utils/utilities';
import { SelectOption } from '../../../utils/models';
import { getAffirmativeOptions, getResultActionOptions } from '../../../utils/data';


class PDRTReportForm extends Form {
	state = {
		data: {
			zoneNumber: '',
			scanNumber: '',
			source: '',
			sourceSerialNumber: '',		
			location: '',
			nominalThickness: '',
			corrosionAllowance: '',
			thickness: 0,
			rtTechnique: '',
			sourceSize: '',
			sourceActivity: '',
			drtScannerName: '',
			drtScannerSerialNumber: '',
			inspectionDate: new Date(),
			drtScannerCalibrationDate: new Date(),
			sourceConformityInspectionDate: new Date(),
			imageFilmType: '',
			pipeDia: 0,
			contrastUsed: '',
			comparatorLocation: '',
			comparatorBallSize: '',
			sdd: '',
			odd: '',
			exposureTime: '',
			sourceManufacturer: '',
			materialSpecification: '',
			result: '',
			limitation: '',
			remark: '',
			image: [],
		},
		errors: {},
		loading: false,
		progress: 0,
	};

	schema = {
		id: Joi.number().optional().label('ID'),
		zoneNumber: Joi.string().min(3).required().label('Zone Number'),
		scanNumber: Joi.string().min(3).required().label('Scan Number'),
		source: Joi.string().min(3).required().label('Source'),
		sourceSerialNumber: Joi.string().required().label('Source Serial Number'),
		location: Joi.string().required().label('Location'),
		nominalThickness: Joi.alternatives().try(Joi.number().positive(), Joi.string().valid('NG').insensitive()).required().label('Nominal Thickness'),
		corrosionAllowance: Joi.alternatives().try(Joi.number().positive(), Joi.string().valid('NG').insensitive()).required().label('Corrosion Allowance'),
		thickness: Joi.number().positive().required().label('Thickness (mm)'),
		rtTechnique: Joi.string().min(3).required().label('RT Technique'),
		sourceSize: Joi.number().min(0).required().label('Source Size (mm)'),
		sourceActivity: Joi.number().min(0).required().label('Source Activity'),
		drtScannerName: Joi.string().min(3).required().label('DRT Scanner Name'),
		drtScannerSerialNumber: Joi.string().min(3).required().label('Serial Number'),
		inspectionDate: Joi.date().less('now').required().label('Inspection Date'),
		drtScannerCalibrationDate: Joi.date().greater('now').required().label('DRT Scanner Calibration Due Date'),
		sourceConformityInspectionDate: Joi.date().greater('now').required().label('Source Conformity Inspection Date'),
		imageFilmType: Joi.string().min(3).required().label('Image Film Type'),
		pipeDia: Joi.number().positive().required().label('Pipe Dia (Inch)'),
		contrastUsed: Joi.string().min(3).required().label('Contrast Used'),
		comparatorLocation: Joi.string().min(3).required().label('Comparator Location'),
		comparatorBallSize: Joi.number().min(0).required().label('Comparator Ball Size (mm)'),
		sdd: Joi.number().min(1).required().label('SDD (cm)'),
		odd: Joi.number().min(1).required().label('ODD (cm)'),
		exposureTime: Joi.number().min(1).required().label('Exposure Time (Minutes)'),
		sourceManufacturer: Joi.string().min(3).required().label('Source Manufacturer'),
		materialSpecification: Joi.string().min(3).required().label('Material Specification'),
		result: Joi.string().min(3).required().label('Result'),
		limitation: Joi.string().optional().label('Limitation'),
		remark: Joi.string().optional().label('Remark'),
		image: Joi.array().min(1).max(IMAGE_LIMITS.DRT_PHOTO).required().label('DRT Image'),
	};

	static propTypes = {
		initialPDRTItem: PropTypes.object,
		onDeleteItem: PropTypes.func,
		onSave: PropTypes.func,
	};
	
	componentDidMount(): void {
		if (this.props.initialPDRTItem) {
			const data = { ...this.props.initialPDRTItem };

			if (data.calibrationDate) {
				if (!data.drtScannerCalibrationDate) data.drtScannerCalibrationDate = data.calibrationDate;
				if (!data.sourceConformityInspectionDate) data.sourceConformityInspectionDate = data.calibrationDate;

				data.calibrationDueDate = undefined;
			}

			this.setState({ data });
		}
	}

	doSubmit = (): void => {
		this.props.onSave(this.state.data);

		if (this.props.initialPDRTItem) return;

		this.setState({
			data: {
				zoneNumber: '',
				scanNumber: '',
				source: '',
				sourceSerialNumber: '',		
				location: '',
				nominalThickness: '',
				corrosionAllowance: '',
				thickness: 0,
				rtTechnique: '',
				sourceSize: '',
				sourceActivity: '',
				drtScannerName: '',
				drtScannerSerialNumber: '',
				inspectionDate: new Date(),
				drtScannerCalibrationDate: new Date(),
				sourceConformityInspectionDate: new Date(),
				imageFilmType: '',
				pipeDia: 0,
				contrastUsed: '',
				sdd: '',
				odd: '',
				exposureTime: '',
				sourceManufacturer: '',
				materialSpecification: '',
				result: '',
				limitation: '',
				remark: '',
				image: [],
			},
		});
	};

	renderSourceActivityUnit = () => {
		const source = SOURCES.find((source) => source.unit === this.state.data.source);
		return source ? `(${source.unit})` : '';
	};

	render() {
		return (
			<form autoComplete="off" onSubmit={this.handleSubmit}>
				<div className="row">
					<div className="col">
						{this.renderInput({
							type: 'text',
							name: 'zoneNumber',
							label: 'Zone Number',
							placeholder: 'Enter Zone Number',
						})}
					</div>

					<div className="col">
						{this.renderInput({
							type: 'text',
							name: 'scanNumber',
							label: 'Scan Number',
							placeholder: 'Enter Scan Number',
						})}
					</div>

					<div className="col">
						{this.renderSelect({
							options: SOURCES,
							name: 'source',
							label: 'Source',
							placeholder: 'Enter Source',
						})}
					</div>

					<div className="col">
						{this.renderInput({
							type: 'text',
							name: 'sourceSerialNumber',
							label: 'Source Serial Number',
							placeholder: 'Enter Source Serial Number',
						})}
					</div>
				</div>

				<div className="my-4 divider"></div>
				
				<div className="row">
					<div className="col">
						{this.renderSelect({
							options: LOCATION_OPTIONS,
							name: 'location',
							label: 'Location',
							placeholder: 'Select Location',
						})}
					</div>

					<div className="col">
						{this.renderInput({
							type: 'text',
							name: 'nominalThickness',
							label: 'Nominal Thickness (MM)',
							placeholder: 'Enter Nominal Thickness',
						})}
					</div>
					
					<div className="col">
						{this.renderInput({
							type: 'text',
							name: 'comparatorLocation',
							label: 'Comparator Location',
							placeholder: 'Enter Comparator Location',
						})}
					</div>
					
					<div className="col">
						{this.renderSelect({
							options: COMPARATOR_SIZES,
							name: 'comparatorBallSize',
							label: 'Comparator Ball Size (mm)',
							placeholder: 'Enter Comparator Ball Size (mm)',
						})}
					</div>

					<div className="col">
						{this.renderInput({
							type: 'text',
							name: 'corrosionAllowance',
							label: 'Corrosion Allowance (MM)',
							placeholder: 'Enter Corrosion Allowance',
						})}
					</div>

					<div className="col">
						{this.renderInput({
							type: 'number',
							name: 'thickness',
							label: 'Measured Thickness (MM)',
							placeholder: 'Enter Measured Thickness (mm)',
						})}
					</div>
				</div>
				
				<div className="my-4 divider"></div>
				
				<div className="row">
					<div className="col">
						{this.renderSelect({
							options: RT_TECHNIQUES,
							name: 'rtTechnique',
							label: 'RT Technique',
							placeholder: 'Enter RT Technique',
						})}
					</div>

					<div className="col">
						{this.renderInput({
							type: 'text',
							name: 'sourceSize',
							label: 'Source Size (mm)',
							placeholder: 'Enter Source Size',
						})}
					</div>

					<div className="col">
						{this.renderSelect({
							options: SOURCE_ACTIVITY,
							name: 'sourceActivity',
							label: 'Source Activity ' + this.renderSourceActivityUnit(),
							placeholder: 'Enter Source Activity',
						})}
					</div>

					<div className="col">
						{this.renderSelect({
							name: 'result',
							label: 'Result',
							placeholder: 'Select Result',
							options: getResultActionOptions(),
						})}
					</div>
				</div>

				<div className="my-4 divider"></div>

				<div className="row">
					<div className="col">
						{this.renderInput({
							type: 'text',
							name: 'drtScannerName',
							label: 'DRT Scanner Name',
							placeholder: 'Enter DRT Scanner Name',
						})}
					</div>

					<div className="col">
						{this.renderInput({
							type: 'text',
							name: 'drtScannerSerialNumber',
							label: 'DRT Scanner Serial Number',
							placeholder: 'Enter DRT Scanner Serial Number',
						})}
					</div>

					<div className="col">
						{this.renderDatePicker({
							name: 'inspectionDate',
							label: 'Inspection Date',
							placeholderText: 'Select Inspection Date',
							maxDate: new Date(),
						})}
					</div>

					<div className="col">
						{this.renderDatePicker({
							name: 'drtScannerCalibrationDate',
							label: 'DRT Scanner Calibration Due Date',
							placeholderText: 'DRT Scanner Calibration Due Date',
							minDate: new Date(),
						})}
					</div>
					
					<div className="col">
						{this.renderDatePicker({
							name: 'sourceConformityInspectionDate',
							label: 'Source Conformity Inspection Date',
							placeholderText: 'Source Conformity Inspection Date',
							minDate: new Date(),
						})}
					</div>

					<div className="col">
						{this.renderSelect({
							options: IMAGE_TYPE_SELECTION,
							name: 'imageFilmType',
							label: 'Image Type',
							placeholder: 'Select Image Type',
						})}
					</div>

					<div className="col">
						{this.renderInput({
							type: 'number',
							name: 'pipeDia',
							label: 'Pipe Dia (Inch)',
							placeholder: 'Enter Pipe Dia (Inch)',
						})}
					</div>
				</div>

				<div className="my-4 divider"></div>

				<div className="row">
					<div className="col">
						{this.renderSelect({
							options: getAffirmativeOptions(),
							name: 'contrastUsed',
							label: 'Contrast Used',
							placeholder: 'Select Contrast Used',
						})}
					</div>

					<div className="col">
						{this.renderInput({
							type: 'text',
							name: 'sdd',
							label: 'SDD (cm)',
							placeholder: 'Enter SDD (cm) Value',
						})}
					</div>

					<div className="col">
						{this.renderInput({
							type: 'text',
							name: 'odd',
							label: 'ODD (cm)',
							placeholder: 'Enter ODD (cm) Value',
						})}
					</div>

					<div className="col">
						{this.renderInput({
							type: 'text',
							name: 'exposureTime',
							label: 'Exposure Time (Minutes)',
							placeholder: 'Enter Exposure Time',
						})}
					</div>

					<div className="col">
						{this.renderSelect({
							options: SOURCE_MANUFACTURERS,
							name: 'sourceManufacturer',
							label: 'Source Manufacturer',
							placeholder: 'Enter Source Manufacturer',
						})}
					</div>

					<div className="col">
						{this.renderSelect({
							options: MATERIAL_OPTIONS,
							name: 'materialSpecification',
							label: 'Material Specification',
							placeholder: 'Enter Material Specification',
						})}
					</div>
				</div>

				<div className="my-4 divider"></div>

				<div className="my-4 divider"></div>

				<div className="row">
					<div className="col">
						{this.renderTextArea({
							name: 'limitation',
							placeholder: 'Enter Limitation (If any)',
							label: 'Limitation (If any)',
							rows: '6',
						})}
					</div>
					<div className="col">
						{this.renderTextArea({
							name: 'remark',
							placeholder: 'Enter Remarks',
							label: 'Remarks',
							rows: '6',
						})}
					</div>
				</div>

				<div className="my-4 divider"></div>

				{this.renderFileInput({
					max: IMAGE_LIMITS.DRT_PHOTO,
					multiple: IMAGE_LIMITS.DRT_PHOTO > 1,
					name: 'image',
					accept: 'image/*,application/pdf',
					label: 'DRT Image',
					sublabel: 'Upload DRT Photo',
					types: getFileUploadTypes(),
					enforceImageCaptionUpdate: false
				})}

				<div className="my-4 divider"></div>

				<div className="d-flex justify-content-end">
                    <Conditional isVisible={!!this.props.onDeleteItem}>
                        <div className="me-3">
                            <Button
                                onClick={this.props.onDeleteItem} 
                                icon="trash"
                                label="Delete" 
								type="button"
                                style={{ backgroundColor: 'red' }} 
                            />
                        </div>
                    </Conditional>

				    {this.renderButton('Save')}
                </div>
			</form>
		);
	}
}

const LOCATION_OPTIONS: SelectOption[] = [
	{
		id: 'Pipe',
		name: 'Pipe'
	},
	{
		id: 'Elbow',
		name: 'Elbow'
	},
	{
		id: 'Valve',
		name: 'Valve'
	},
	{
		id: 'T',
		name: 'T'
	},
	{
		id: 'Flange',
		name: 'Flange'
	},
	{
		id: 'Plate',
		name: 'Plate'
	},
];

const COMPARATOR_SIZES = _.range(1, 26).filter((value) => value % 5 === 0).map((value) => ({
	id: value,
	name: value,
}));

export default PDRTReportForm;
